import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Package,
  Store,
  Plus,
  Trash2,
  Edit,
  Clock,
  CheckCircle,
  Truck,
  AlertCircle,
  Filter,
  Search,
  Warehouse,
  ChevronRight,
  ChevronDown,
  RotateCcw,
  Send,
  Save,
  X,
  ShoppingCart,
  Building2,
  Undo2
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ORDER_LINE_STATUSES = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
  pedido_proveedor: { label: 'Pedido a proveedor', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: ShoppingCart },
  prepared: { label: 'Preparado', color: 'bg-purple-100 text-purple-800 border-purple-200', icon: CheckCircle },
  shipped: { label: 'Enviado', color: 'bg-green-100 text-green-800 border-green-200', icon: Truck },
  received: { label: 'Recibido', color: 'bg-slate-100 text-slate-800 border-slate-200', icon: CheckCircle }
};

const SUPPLIER_STATUSES = {
  pending: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
  ordered: { label: 'Pedido realizado', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: ShoppingCart },
  in_transit: { label: 'En tránsito', color: 'bg-indigo-100 text-indigo-800 border-indigo-200', icon: Truck },
  received: { label: 'Recibido', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-800 border-red-200', icon: X },
};

export default function InternalOrders() {
  const { isAdmin, hasPermission, user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('orders');
  
  // Data
  const [stores, setStores] = useState([]);
  const [orders, setOrders] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [warehouseSummary, setWarehouseSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [selectedStore, setSelectedStore] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [supplierFilter, setSupplierFilter] = useState('all');
  
  // Dialogs
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [storeForm, setStoreForm] = useState({ name: '', description: '' });
  
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [newOrderStore, setNewOrderStore] = useState('');
  const [newOrderLines, setNewOrderLines] = useState([]);
  const [newOrderNotes, setNewOrderNotes] = useState('');
  
  // Edit order state
  const [editOrderDialogOpen, setEditOrderDialogOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editOrderLines, setEditOrderLines] = useState([]);
  const [editOrderNotes, setEditOrderNotes] = useState('');
  const [savingOrder, setSavingOrder] = useState(false);
  
  const [deleteStoreId, setDeleteStoreId] = useState(null);
  const [deleteOrderId, setDeleteOrderId] = useState(null);
  
  // Expanded orders
  const [expandedOrders, setExpandedOrders] = useState({});

  // Supplier orders
  const [supplierOrders, setSupplierOrders] = useState([]);
  const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [supplierForm, setSupplierForm] = useState({
    supplier: '', product_description: '', quantity: 1,
    responsible_user_id: '', responsible_user_name: '',
    expected_date: '', estimated_price: '', notes: ''
  });
  const [supplierStatusFilter, setSupplierStatusFilter] = useState('all');
  const [deleteSupplierOrder, setDeleteSupplierOrder] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [users, setUsers] = useState([]);

  // Critical stock
  const [criticalStock, setCriticalStock] = useState([]);
  const [criticalDialogOpen, setCriticalDialogOpen] = useState(false);
  const [editingCritical, setEditingCritical] = useState(null);
  const [criticalForm, setCriticalForm] = useState({
    material_id: '', product_name: '', minimum_quantity: 1,
    current_stock: 0, supplier_name: '', category: '', notes: ''
  });
  const [collapsedCategories, setCollapsedCategories] = useState({});

  // Store Stock targets
  const [storeStock, setStoreStock] = useState([]);
  const [storeStockDialogOpen, setStoreStockDialogOpen] = useState(false);
  const [editingStoreStock, setEditingStoreStock] = useState(null);
  const [storeStockForm, setStoreStockForm] = useState({ product_name: '', notes: '', targets: {} });
  const [deleteStoreStockId, setDeleteStoreStockId] = useState(null);
  const [storeStockSearch, setStoreStockSearch] = useState('');

  const canManageWarehouse = isAdmin || hasPermission('warehouse.manage');
  const canManageStores = isAdmin || hasPermission('stores.manage');

  useEffect(() => {
    fetchStores();
    fetchOrders();
    fetchMaterials();
    fetchSupplierOrders();
    fetchUsers();
    fetchCriticalStock();
    fetchStoreStock();
    if (canManageWarehouse) {
      fetchWarehouseSummary();
    }
  }, []);

  const fetchStores = async () => {
    try {
      const response = await axios.get(`${API}/stores`);
      setStores(response.data);
    } catch (error) {
      console.error('Error fetching stores:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/internal-orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/materials`);
      setMaterials(response.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
    }
  };

  const fetchWarehouseSummary = async () => {
    try {
      const response = await axios.get(`${API}/internal-orders/warehouse/summary`);
      setWarehouseSummary(response.data);
    } catch (error) {
      console.error('Error fetching warehouse summary:', error);
    }
  };

  const fetchSupplierOrders = async () => {
    try {
      const res = await axios.get(`${API}/supplier-orders`);
      setSupplierOrders(res.data);
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch {}
  };

  const fetchCriticalStock = async () => {
    try {
      const res = await axios.get(`${API}/critical-stock`);
      setCriticalStock(res.data);
    } catch {}
  };

  const openCriticalDialog = (item = null) => {
    if (item) {
      setEditingCritical(item);
      setCriticalForm({
        material_id: item.material_id || '', product_name: item.product_name,
        minimum_quantity: item.minimum_quantity, current_stock: item.current_stock,
        supplier_name: item.supplier_name || '', category: item.category || '',
        notes: item.notes || ''
      });
    } else {
      setEditingCritical(null);
      setCriticalForm({ material_id: '', product_name: '', minimum_quantity: 1, current_stock: 0, supplier_name: '', category: '', notes: '' });
    }
    setCriticalDialogOpen(true);
  };

  const saveCriticalStock = async () => {
    if (!criticalForm.product_name.trim()) return toast.error('Nombre del producto obligatorio');
    try {
      if (editingCritical) {
        await axios.put(`${API}/critical-stock/${editingCritical.id}`, criticalForm);
        toast.success('Item actualizado');
      } else {
        await axios.post(`${API}/critical-stock`, criticalForm);
        toast.success('Item añadido al control de stock');
      }
      setCriticalDialogOpen(false);
      fetchCriticalStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const deleteCriticalStock = async (id) => {
    try {
      await axios.delete(`${API}/critical-stock/${id}`);
      toast.success('Item eliminado');
      fetchCriticalStock();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const updateCriticalStockField = async (id, field, value) => {
    try {
      await axios.put(`${API}/critical-stock/${id}`, { [field]: value });
      fetchCriticalStock();
    } catch {}
  };

  // ===== Store Stock targets =====
  const fetchStoreStock = async () => {
    try {
      const res = await axios.get(`${API}/store-stock`);
      setStoreStock(res.data);
    } catch {}
  };

  const openStoreStockDialog = (item = null) => {
    if (item) {
      setEditingStoreStock(item);
      setStoreStockForm({
        product_name: item.product_name,
        notes: item.notes || '',
        targets: { ...(item.targets || {}) },
      });
    } else {
      setEditingStoreStock(null);
      setStoreStockForm({ product_name: '', notes: '', targets: {} });
    }
    setStoreStockDialogOpen(true);
  };

  const saveStoreStock = async () => {
    if (!storeStockForm.product_name.trim()) return toast.error('Nombre del producto obligatorio');
    try {
      const payload = {
        product_name: storeStockForm.product_name.trim(),
        notes: storeStockForm.notes,
        targets: Object.fromEntries(
          Object.entries(storeStockForm.targets || {}).map(([k, v]) => [k, parseInt(v) || 0])
        ),
      };
      if (editingStoreStock) {
        await axios.put(`${API}/store-stock/${editingStoreStock.id}`, payload);
        toast.success('Producto actualizado');
      } else {
        await axios.post(`${API}/store-stock`, payload);
        toast.success('Producto añadido');
      }
      setStoreStockDialogOpen(false);
      fetchStoreStock();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const updateStoreStockTarget = async (itemId, location, qty) => {
    try {
      const value = parseInt(qty);
      const safeVal = Number.isNaN(value) ? 0 : value;
      await axios.patch(`${API}/store-stock/${itemId}/target`, { location, quantity: safeVal });
      // Update local state without full refetch for snappier UX
      setStoreStock(prev => prev.map(it => (
        it.id === itemId
          ? { ...it, targets: { ...(it.targets || {}), [location]: safeVal } }
          : it
      )));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
      fetchStoreStock();
    }
  };

  const deleteStoreStock = async () => {
    if (!deleteStoreStockId) return;
    try {
      await axios.delete(`${API}/store-stock/${deleteStoreStockId}`);
      toast.success('Producto eliminado');
      setDeleteStoreStockId(null);
      fetchStoreStock();
    } catch {
      toast.error('Error al eliminar');
    }
  };

  const criticalAlerts = criticalStock.filter(i => i.alert);

  // Group critical stock by category
  const criticalByCategory = criticalStock.reduce((acc, item) => {
    const cat = item.category || 'Sin categoría';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const toggleCategory = (cat) => {
    setCollapsedCategories(prev => ({ ...prev, [cat]: !prev[cat] }));
  };

  const openSupplierDialog = (order = null) => {
    if (order) {
      setEditingSupplier(order);
      setSupplierForm({
        supplier: order.supplier, product_description: order.product_description,
        quantity: order.quantity, responsible_user_id: order.responsible_user_id,
        responsible_user_name: order.responsible_user_name,
        expected_date: order.expected_date || '', estimated_price: order.estimated_price || '',
        notes: order.notes || ''
      });
    } else {
      setEditingSupplier(null);
      setSupplierForm({
        supplier: '', product_description: '', quantity: 1,
        responsible_user_id: user?.id || '', responsible_user_name: user?.name || '',
        expected_date: '', estimated_price: '', notes: ''
      });
    }
    setSupplierDialogOpen(true);
  };

  const handleSaveSupplier = async () => {
    if (!supplierForm.supplier || !supplierForm.product_description || !supplierForm.responsible_user_id) {
      return toast.error('Proveedor, producto y responsable son obligatorios');
    }
    try {
      const payload = {
        ...supplierForm,
        quantity: Number(supplierForm.quantity) || 1,
        estimated_price: supplierForm.estimated_price ? Number(supplierForm.estimated_price) : null,
        expected_date: supplierForm.expected_date || null,
      };
      if (editingSupplier) {
        await axios.put(`${API}/supplier-orders/${editingSupplier.id}`, payload);
        toast.success('Pedido a proveedor actualizado');
      } else {
        await axios.post(`${API}/supplier-orders`, payload);
        toast.success('Pedido a proveedor creado');
      }
      setSupplierDialogOpen(false);
      fetchSupplierOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleSupplierStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/supplier-orders/${orderId}`, { status: newStatus });
      toast.success('Estado actualizado');
      fetchSupplierOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDeleteSupplier = async () => {
    if (!deleteSupplierOrder) return;
    if ((isSuperAdmin || isAdmin) && !deleteReason.trim()) {
      return toast.error('El motivo de eliminación es obligatorio');
    }
    try {
      await axios.delete(`${API}/supplier-orders/${deleteSupplierOrder.id}?reason=${encodeURIComponent(deleteReason)}`);
      toast.success('Pedido eliminado');
      setDeleteSupplierOrder(null);
      setDeleteReason('');
      fetchSupplierOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleRestoreSupplier = async (orderId) => {
    try {
      await axios.put(`${API}/supplier-orders/${orderId}/restore`);
      toast.success('Pedido restaurado');
      fetchSupplierOrders();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  // Store CRUD
  const openStoreDialog = (store = null) => {
    if (store) {
      setEditingStore(store);
      setStoreForm({ name: store.name, description: store.description || '' });
    } else {
      setEditingStore(null);
      setStoreForm({ name: '', description: '' });
    }
    setStoreDialogOpen(true);
  };

  const handleSaveStore = async () => {
    if (!storeForm.name.trim()) {
      toast.error('El nombre es obligatorio');
      return;
    }
    try {
      if (editingStore) {
        await axios.put(`${API}/stores/${editingStore.id}`, storeForm);
        toast.success('Tienda actualizada');
      } else {
        await axios.post(`${API}/stores`, storeForm);
        toast.success('Tienda creada');
      }
      setStoreDialogOpen(false);
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDeleteStore = async () => {
    try {
      await axios.delete(`${API}/stores/${deleteStoreId}`);
      toast.success('Tienda eliminada');
      setDeleteStoreId(null);
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Order CRUD
  const openNewOrderDialog = () => {
    setNewOrderStore('');
    setNewOrderLines([{ 
      material_id: '', 
      material_name: '', 
      category: 'Sin categoría', 
      quantity: 1, 
      priority: 'normal', 
      comments: '' 
    }]);
    setNewOrderNotes('');
    setOrderDialogOpen(true);
  };

  const addOrderLine = () => {
    setNewOrderLines([...newOrderLines, { 
      material_id: '', 
      material_name: '', 
      category: 'Sin categoría', 
      quantity: 1, 
      priority: 'normal', 
      comments: '' 
    }]);
  };

  const updateOrderLine = (index, field, value) => {
    const updated = [...newOrderLines];
    updated[index][field] = value;
    
    // If selecting from material list, auto-fill name and category
    if (field === 'material_id' && value) {
      const mat = materials.find(m => m.id === value);
      if (mat) {
        updated[index].material_name = mat.name;
        updated[index].category = mat.category || 'Sin categoría';
      }
    }
    
    setNewOrderLines(updated);
  };

  const removeOrderLine = (index) => {
    setNewOrderLines(newOrderLines.filter((_, i) => i !== index));
  };

  const handleCreateOrder = async () => {
    if (!newOrderStore) {
      toast.error('Selecciona una tienda');
      return;
    }
    
    const validLines = newOrderLines.filter(l => l.material_name.trim());
    if (validLines.length === 0) {
      toast.error('Añade al menos un material');
      return;
    }

    try {
      await axios.post(`${API}/internal-orders`, {
        store_id: newOrderStore,
        lines: validLines,
        notes: newOrderNotes
      });
      toast.success('Pedido creado');
      setOrderDialogOpen(false);
      fetchOrders();
      if (canManageWarehouse) fetchWarehouseSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear pedido');
    }
  };

  const handleDeleteOrder = async () => {
    try {
      await axios.delete(`${API}/internal-orders/${deleteOrderId}`);
      toast.success('Pedido eliminado');
      setDeleteOrderId(null);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  // Edit order functions
  const openEditOrderDialog = (order) => {
    setEditingOrder(order);
    setEditOrderLines(order.lines.map(l => ({
      ...l,
      _isNew: false,
      _isModified: false
    })));
    setEditOrderNotes(order.notes || '');
    setEditOrderDialogOpen(true);
  };

  const addEditOrderLine = () => {
    setEditOrderLines([...editOrderLines, {
      id: `new_${Date.now()}`,
      material_id: '',
      material_name: '',
      category: 'Sin categoría',
      quantity: 1,
      priority: 'normal',
      comments: '',
      status: 'pending',
      _isNew: true
    }]);
  };

  const updateEditOrderLine = (index, field, value) => {
    const updated = [...editOrderLines];
    updated[index][field] = value;
    updated[index]._isModified = true;
    
    if (field === 'material_id' && value) {
      const mat = materials.find(m => m.id === value);
      if (mat) {
        updated[index].material_name = mat.name;
        updated[index].category = mat.category || 'Sin categoría';
      }
    }
    
    setEditOrderLines(updated);
  };

  const removeEditOrderLine = (index) => {
    const lineToRemove = editOrderLines[index];
    if (lineToRemove._isNew) {
      // Just remove from local state
      setEditOrderLines(editOrderLines.filter((_, i) => i !== index));
    } else {
      // Mark for deletion
      const updated = [...editOrderLines];
      updated[index]._toDelete = true;
      setEditOrderLines(updated);
    }
  };

  const handleSaveEditedOrder = async () => {
    if (!editingOrder) return;
    setSavingOrder(true);
    
    try {
      // 1. Update notes if changed
      if (editOrderNotes !== editingOrder.notes) {
        await axios.put(`${API}/internal-orders/${editingOrder.id}`, {
          notes: editOrderNotes
        });
      }
      
      // 2. Process line changes
      for (const line of editOrderLines) {
        if (line._toDelete && !line._isNew) {
          // Delete existing line
          await axios.delete(`${API}/internal-orders/${editingOrder.id}/lines/${line.id}`);
        } else if (line._isNew && !line._toDelete) {
          // Add new line
          if (line.material_name.trim()) {
            await axios.post(`${API}/internal-orders/${editingOrder.id}/lines`, {
              material_id: line.material_id || null,
              material_name: line.material_name,
              category: line.category,
              quantity: line.quantity,
              priority: line.priority,
              comments: line.comments
            });
          }
        } else if (line._isModified && !line._isNew && !line._toDelete) {
          // Update existing line
          await axios.put(`${API}/internal-orders/${editingOrder.id}/lines/${line.id}`, {
            material_name: line.material_name,
            category: line.category,
            quantity: line.quantity,
            priority: line.priority,
            comments: line.comments
          });
        }
      }
      
      toast.success('Pedido actualizado');
      setEditOrderDialogOpen(false);
      setEditingOrder(null);
      fetchOrders();
      if (canManageWarehouse) fetchWarehouseSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar cambios');
    } finally {
      setSavingOrder(false);
    }
  };

  // Line status updates
  const updateLineStatus = async (orderId, lineId, newStatus, extraData = {}) => {
    try {
      await axios.put(`${API}/internal-orders/${orderId}/lines/${lineId}`, { status: newStatus, ...extraData });
      toast.success('Estado actualizado');
      fetchOrders();
      if (canManageWarehouse) fetchWarehouseSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  const bulkUpdateStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/internal-orders/${orderId}/lines/bulk-status`, { new_status: newStatus });
      toast.success('Estados actualizados');
      fetchOrders();
      if (canManageWarehouse) fetchWarehouseSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar');
    }
  };

  // Filtering
  // Extract unique categories and suppliers from all order lines
  const allCategories = [...new Set(orders.flatMap(o => o.lines.map(l => l.category)).filter(Boolean))].sort();
  const allSuppliers = [...new Set(orders.flatMap(o => o.lines.map(l => l.supplier_name).filter(Boolean)))].sort();

  const filteredOrders = orders.filter(order => {
    if (selectedStore !== 'all' && order.store_id !== selectedStore) return false;
    if (statusFilter !== 'all') {
      const hasMatchingLine = order.lines.some(l => l.status === statusFilter);
      if (!hasMatchingLine) return false;
    }
    if (priorityFilter !== 'all') {
      const hasMatchingLine = order.lines.some(l => l.priority === priorityFilter);
      if (!hasMatchingLine) return false;
    }
    if (categoryFilter !== 'all') {
      const hasMatchingLine = order.lines.some(l => l.category === categoryFilter);
      if (!hasMatchingLine) return false;
    }
    if (supplierFilter !== 'all') {
      const hasMatchingLine = order.lines.some(l => l.supplier_name === supplierFilter);
      if (!hasMatchingLine) return false;
    }
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesMaterial = order.lines.some(l => l.material_name.toLowerCase().includes(search));
      const matchesStore = order.store_name.toLowerCase().includes(search);
      if (!matchesMaterial && !matchesStore) return false;
    }
    return true;
  });

  const hasActiveFilters = selectedStore !== 'all' || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all' || supplierFilter !== 'all' || searchTerm;
  const clearAllFilters = () => {
    setSelectedStore('all');
    setStatusFilter('all');
    setPriorityFilter('all');
    setCategoryFilter('all');
    setSupplierFilter('all');
    setSearchTerm('');
  };

  const toggleOrderExpanded = (orderId) => {
    setExpandedOrders(prev => ({ ...prev, [orderId]: !prev[orderId] }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = ORDER_LINE_STATUSES[status] || ORDER_LINE_STATUSES.pending;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1`}>
        <Icon size={12} />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="internal-orders-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold font-['Barlow_Condensed'] tracking-tight text-slate-900">
            Pedidos Internos
          </h1>
          <p className="text-slate-500 mt-1">Gestión de pedidos entre tiendas y almacén</p>
        </div>
        <Button 
          onClick={openNewOrderDialog}
          className="bg-orange-500 hover:bg-orange-600 text-white gap-2"
          data-testid="new-order-btn"
        >
          <Plus size={18} />
          Nuevo Pedido
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('orders')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'orders' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-orders"
        >
          <span className="flex items-center gap-2">
            <Package size={16} />
            Pedidos
          </span>
          {activeTab === 'orders' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
        </button>
        
        {canManageWarehouse && (
          <button
            onClick={() => setActiveTab('warehouse')}
            className={`px-4 py-2 font-medium text-sm transition-colors relative ${
              activeTab === 'warehouse' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-warehouse"
          >
            <span className="flex items-center gap-2">
              <Warehouse size={16} />
              Vista Almacén
              {warehouseSummary?.urgent_count > 0 && (
                <Badge className="bg-red-500 text-white text-xs px-1.5">{warehouseSummary.urgent_count}</Badge>
              )}
            </span>
            {activeTab === 'warehouse' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
          </button>
        )}
        
        {canManageStores && (
          <button
            onClick={() => setActiveTab('stores')}
            className={`px-4 py-2 font-medium text-sm transition-colors relative ${
              activeTab === 'stores' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-stores"
          >
            <span className="flex items-center gap-2">
              <Store size={16} />
              Tiendas
            </span>
            {activeTab === 'stores' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
          </button>
        )}

        <button
          onClick={() => setActiveTab('supplier')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'supplier' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-supplier"
        >
          <span className="flex items-center gap-2">
            <Building2 size={16} />
            Proveedor
            {supplierOrders.length > 0 && (
              <Badge className="bg-indigo-100 text-indigo-700 text-xs px-1.5 py-0">{supplierOrders.length}</Badge>
            )}
          </span>
          {activeTab === 'supplier' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
        </button>

        <button
          onClick={() => setActiveTab('critical')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'critical' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-critical"
        >
          <span className="flex items-center gap-2">
            <AlertCircle size={16} />
            Material Crítico
            {criticalAlerts.length > 0 && (
              <Badge className="bg-red-500 text-white text-xs px-1.5 py-0">{criticalAlerts.length}</Badge>
            )}
          </span>
          {activeTab === 'critical' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
        </button>

        <button
          onClick={() => setActiveTab('store-stock')}
          className={`px-4 py-2 font-medium text-sm transition-colors relative ${
            activeTab === 'store-stock' ? 'text-orange-600' : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-store-stock"
        >
          <span className="flex items-center gap-2">
            <Warehouse size={16} />
            Stock por Tiendas
          </span>
          {activeTab === 'store-stock' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />}
        </button>
      </div>

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 uppercase">Buscar</Label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Material o tienda..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>
            <div className="w-48">
              <Label className="text-xs text-slate-500 uppercase">Tienda</Label>
              <Select value={selectedStore} onValueChange={setSelectedStore}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las tiendas</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-40">
              <Label className="text-xs text-slate-500 uppercase">Estado</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="pending">Pendiente</SelectItem>
                  <SelectItem value="pedido_proveedor">Pedido a proveedor</SelectItem>
                  <SelectItem value="prepared">Preparado</SelectItem>
                  <SelectItem value="shipped">Enviado</SelectItem>
                  <SelectItem value="received">Recibido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-36">
              <Label className="text-xs text-slate-500 uppercase">Prioridad</Label>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger data-testid="filter-priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="urgent">Urgente</SelectItem>
                  <SelectItem value="normal">Normal</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {allCategories.length > 0 && (
              <div className="w-40">
                <Label className="text-xs text-slate-500 uppercase">Categoría</Label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger data-testid="filter-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas</SelectItem>
                    {allCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {allSuppliers.length > 0 && (
              <div className="w-40">
                <Label className="text-xs text-slate-500 uppercase">Proveedor</Label>
                <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                  <SelectTrigger data-testid="filter-supplier">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {allSuppliers.map(sup => (
                      <SelectItem key={sup} value={sup}>{sup}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-slate-500 hover:text-slate-700 self-end"
                data-testid="clear-filters-btn">
                <X size={14} className="mr-1" /> Limpiar
              </Button>
            )}
          </div>

          {/* Orders List */}
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner w-8 h-8" />
            </div>
          ) : filteredOrders.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Package size={48} className="mb-4" />
                <p className="text-lg">No hay pedidos</p>
                <p className="text-sm">Crea un nuevo pedido para empezar</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredOrders.map(order => (
                <Card key={order.id} className="border-slate-200" data-testid={`order-${order.id}`}>
                  <CardHeader 
                    className="cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleOrderExpanded(order.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        {expandedOrders[order.id] ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                        <div>
                          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                            <Store size={16} className="text-orange-500" />
                            {order.store_name}
                            {order.lines.some(l => l.priority === 'urgent' && l.status !== 'received') && (
                              <Badge className="bg-red-100 text-red-700 border-red-200">
                                <AlertCircle size={12} className="mr-1" />
                                Urgente
                              </Badge>
                            )}
                          </CardTitle>
                          <p className="text-sm text-slate-500">
                            {formatDate(order.created_at)} · {order.created_by_name} · {order.lines.length} líneas
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          {Object.entries(ORDER_LINE_STATUSES).map(([status, config]) => {
                            const count = order.lines.filter(l => l.status === status).length;
                            if (count === 0) return null;
                            return (
                              <Badge key={status} className={`${config.color} text-xs`}>
                                {count}
                              </Badge>
                            );
                          })}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-blue-500 hover:text-blue-600"
                          onClick={(e) => { e.stopPropagation(); openEditOrderDialog(order); }}
                          data-testid={`edit-order-${order.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-600"
                          onClick={(e) => { e.stopPropagation(); setDeleteOrderId(order.id); }}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  
                  {expandedOrders[order.id] && (
                    <CardContent className="pt-0">
                      {order.notes && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800 whitespace-pre-wrap break-words">
                          <strong>Notas:</strong> {order.notes}
                        </div>
                      )}
                      
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50">
                            <TableHead className="text-xs">Material</TableHead>
                            <TableHead className="text-xs">Categoría</TableHead>
                            <TableHead className="text-xs text-center">Cantidad</TableHead>
                            <TableHead className="text-xs">Prioridad</TableHead>
                            <TableHead className="text-xs">Estado</TableHead>
                            <TableHead className="text-xs">Comentarios</TableHead>
                            {canManageWarehouse && <TableHead className="text-xs">Acciones</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {order.lines.map(line => (
                            <TableRow key={line.id}>
                              <TableCell className="font-medium">{line.material_name}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="bg-slate-50">{line.category}</Badge>
                              </TableCell>
                              <TableCell className="text-center font-semibold">{line.quantity}</TableCell>
                              <TableCell>
                                {line.priority === 'urgent' ? (
                                  <Badge className="bg-red-100 text-red-700 border-red-200">Urgente</Badge>
                                ) : (
                                  <Badge variant="outline">Normal</Badge>
                                )}
                              </TableCell>
                              <TableCell>{getStatusBadge(line.status)}</TableCell>
                              <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">
                                {line.comments || '-'}
                                {line.status === 'pedido_proveedor' && line.supplier_name && (
                                  <div className="text-xs text-blue-600 mt-0.5">
                                    Proveedor: {line.supplier_name} · {line.ordered_by || ''}
                                  </div>
                                )}
                              </TableCell>
                              {canManageWarehouse && (
                                <TableCell>
                                  <Select 
                                    value={line.status}
                                    onValueChange={(v) => updateLineStatus(order.id, line.id, v)}
                                  >
                                    <SelectTrigger className="w-32 h-8 text-xs">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="pending">Pendiente</SelectItem>
                                      <SelectItem value="pedido_proveedor">Pedido a proveedor</SelectItem>
                                      <SelectItem value="prepared">Preparado</SelectItem>
                                      <SelectItem value="shipped">Enviado</SelectItem>
                                      <SelectItem value="received">Recibido</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      
                      {canManageWarehouse && (
                        <div className="flex gap-2 mt-4 pt-4 border-t">
                          <Button size="sm" variant="outline" onClick={() => bulkUpdateStatus(order.id, 'pedido_proveedor')}>
                            <ShoppingCart size={14} className="mr-1" /> Pedido a proveedor
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => bulkUpdateStatus(order.id, 'prepared')}>
                            <CheckCircle size={14} className="mr-1" /> Marcar Preparado
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => bulkUpdateStatus(order.id, 'shipped')}>
                            <Truck size={14} className="mr-1" /> Marcar Enviado
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  )}
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Warehouse Tab */}
      {activeTab === 'warehouse' && canManageWarehouse && (
        <>
          {/* Summary Cards */}
          {warehouseSummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {Object.entries(ORDER_LINE_STATUSES).map(([status, config]) => {
                const Icon = config.icon;
                const count = warehouseSummary.by_status[status] || 0;
                return (
                  <Card key={status} className={`border-slate-200 cursor-pointer hover:shadow-md transition-shadow ${statusFilter === status ? 'ring-2 ring-orange-500' : ''}`}
                    onClick={() => { setStatusFilter(status); setActiveTab('orders'); }}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${config.color}`}>
                          <Icon size={20} />
                        </div>
                        <div>
                          <p className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">{count}</p>
                          <p className="text-xs text-slate-500 uppercase">{config.label}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Urgent Alert */}
          {warehouseSummary?.urgent_count > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 flex items-center gap-4">
                <AlertCircle size={24} className="text-red-500" />
                <div>
                  <p className="font-semibold text-red-800">
                    {warehouseSummary.urgent_count} líneas urgentes pendientes
                  </p>
                  <p className="text-sm text-red-600">Requieren atención inmediata</p>
                </div>
                <Button 
                  className="ml-auto bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => { setPriorityFilter('urgent'); setActiveTab('orders'); }}
                >
                  Ver Urgentes
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Stores with Pending Orders — only render the card when there are pending orders */}
          {warehouseSummary?.stores_pending?.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-xl font-['Barlow_Condensed']">Tiendas con Pedidos Pendientes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {warehouseSummary.stores_pending.map(store => (
                    <div 
                      key={store.store_id}
                      className="bg-slate-50 rounded-lg p-4 flex items-center justify-between cursor-pointer hover:bg-slate-100 transition-colors"
                      onClick={() => { setSelectedStore(store.store_id); setActiveTab('orders'); }}
                    >
                      <div className="flex items-center gap-3">
                        <Store size={20} className="text-orange-500" />
                        <span className="font-medium">{store.store_name}</span>
                      </div>
                      <Badge className="bg-orange-100 text-orange-700">{store.pending_lines} líneas</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Stores Tab */}
      {activeTab === 'stores' && canManageStores && (
        <>
          <div className="flex justify-end">
            <Button onClick={() => openStoreDialog()} className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
              <Plus size={16} />
              Nueva Tienda
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {stores.map(store => (
              <Card key={store.id} className="border-slate-200" data-testid={`store-${store.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
                        <Store size={18} className="text-orange-500" />
                        {store.name}
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1">{store.description || 'Sin descripción'}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openStoreDialog(store)}>
                        <Edit size={14} />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-600"
                        onClick={() => setDeleteStoreId(store.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-slate-400">
                    {orders.filter(o => o.store_id === store.id).length} pedidos
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {stores.length === 0 && (
            <Card className="border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16 text-slate-400">
                <Store size={48} className="mb-4" />
                <p className="text-lg">No hay tiendas</p>
                <p className="text-sm">Crea una tienda para empezar</p>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Supplier Orders Tab */}
      {activeTab === 'supplier' && (
        <>
          <div className="flex flex-wrap gap-3 items-end justify-between">
            <div className="flex gap-2 items-end">
              <div>
                <Label className="text-xs text-slate-500">Estado</Label>
                <Select value={supplierStatusFilter} onValueChange={setSupplierStatusFilter}>
                  <SelectTrigger className="w-40" data-testid="supplier-status-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {Object.entries(SUPPLIER_STATUSES).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Button onClick={() => openSupplierDialog()} className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2" data-testid="new-supplier-order-btn">
              <Plus size={16} /> Pedido a Proveedor
            </Button>
          </div>

          <div className="space-y-3">
            {supplierOrders
              .filter(o => supplierStatusFilter === 'all' || o.status === supplierStatusFilter)
              .map(order => {
                const st = SUPPLIER_STATUSES[order.status] || SUPPLIER_STATUSES.pending;
                const StIcon = st.icon;
                return (
                  <Card key={order.id} className="hover:shadow-md transition-shadow" data-testid={`supplier-order-${order.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <Building2 size={16} className="text-indigo-500 flex-shrink-0" />
                            <span className="font-semibold text-slate-800">{order.supplier}</span>
                            <Badge className={`${st.color} border text-xs`}><StIcon size={12} className="mr-1" />{st.label}</Badge>
                          </div>
                          <p className="text-sm text-slate-600">{order.product_description}</p>
                          <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
                            <span>Cantidad: <strong>{order.quantity}</strong></span>
                            {order.estimated_price && <span>Precio est.: <strong>{Number(order.estimated_price).toLocaleString('es-ES', {style:'currency',currency:'EUR'})}</strong></span>}
                            {order.expected_date && <span>Fecha prevista: <strong>{new Date(order.expected_date + 'T00:00:00').toLocaleDateString('es-ES')}</strong></span>}
                            <span>Responsable: <strong>{order.responsible_user_name || '—'}</strong></span>
                            <span>Creado por: {order.created_by_name} · {new Date(order.created_at).toLocaleDateString('es-ES')}</span>
                          </div>
                          {order.notes && <p className="text-xs text-slate-400 mt-1 italic">"{order.notes}"</p>}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Select value={order.status} onValueChange={(v) => handleSupplierStatusChange(order.id, v)}>
                            <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(SUPPLIER_STATUSES).map(([k, v]) => (
                                <SelectItem key={k} value={k}>{v.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openSupplierDialog(order)} data-testid={`edit-supplier-${order.id}`}>
                            <Edit size={14} />
                          </Button>
                          {(isAdmin || isSuperAdmin || order.created_by_id === user?.id) && (
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 hover:text-red-700" onClick={() => setDeleteSupplierOrder(order)} data-testid={`delete-supplier-${order.id}`}>
                              <Trash2 size={14} />
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            {supplierOrders.filter(o => supplierStatusFilter === 'all' || o.status === supplierStatusFilter).length === 0 && (
              <Card><CardContent className="p-8 text-center text-slate-400">No hay pedidos a proveedor</CardContent></Card>
            )}
          </div>
        </>
      )}

      {/* Critical Stock Tab */}
      {activeTab === 'critical' && (
        <>
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-slate-500">
                Control de stock mínimo · {criticalAlerts.length > 0 ? (
                  <span className="text-red-600 font-medium">{criticalAlerts.length} alerta{criticalAlerts.length !== 1 ? 's' : ''}</span>
                ) : (
                  <span className="text-green-600">Sin alertas</span>
                )}
              </p>
            </div>
            {isAdmin && (
              <Button onClick={() => openCriticalDialog()} className="bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="add-critical-btn">
                <Plus size={16} /> Añadir Material Crítico
              </Button>
            )}
          </div>

          {criticalStock.length === 0 ? (
            <Card><CardContent className="p-8 text-center text-slate-400">No hay materiales en control de stock crítico</CardContent></Card>
          ) : (
            <div className="space-y-3">
              {Object.entries(criticalByCategory).sort(([a], [b]) => a.localeCompare(b)).map(([category, items]) => {
                const isCollapsed = collapsedCategories[category];
                const categoryAlerts = items.filter(i => i.alert).length;
                return (
                  <Card key={category} data-testid={`critical-category-${category}`}>
                    <button
                      onClick={() => toggleCategory(category)}
                      className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors rounded-t-lg"
                      data-testid={`toggle-category-${category}`}
                    >
                      <div className="flex items-center gap-2">
                        {isCollapsed ? <ChevronRight size={18} className="text-slate-400" /> : <ChevronDown size={18} className="text-slate-400" />}
                        <span className="font-semibold text-slate-800">{category}</span>
                        <Badge variant="outline" className="text-xs">{items.length}</Badge>
                        {categoryAlerts > 0 && (
                          <Badge className="bg-red-100 text-red-700 text-xs">{categoryAlerts} alerta{categoryAlerts !== 1 ? 's' : ''}</Badge>
                        )}
                      </div>
                    </button>
                    {!isCollapsed && (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Producto</TableHead>
                            <TableHead>Proveedor</TableHead>
                            <TableHead className="text-center">Stock Actual</TableHead>
                            <TableHead className="text-center">Mínimo</TableHead>
                            <TableHead>Estado</TableHead>
                            <TableHead>Notas</TableHead>
                            {isAdmin && <TableHead className="w-20">Acciones</TableHead>}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.sort((a, b) => a.product_name.localeCompare(b.product_name)).map(item => (
                            <TableRow key={item.id} className={item.alert ? 'bg-red-50' : ''} data-testid={`critical-item-${item.id}`}>
                              <TableCell className="font-medium">{item.product_name}</TableCell>
                              <TableCell className="text-sm text-slate-600">{item.supplier_name || '-'}</TableCell>
                              <TableCell className="text-center">
                                {isAdmin ? (
                                  <Input type="number" min="0" className="w-20 h-7 text-center text-sm mx-auto"
                                    value={item.current_stock}
                                    onChange={(e) => updateCriticalStockField(item.id, 'current_stock', parseInt(e.target.value) || 0)}
                                    data-testid={`critical-stock-${item.id}`} />
                                ) : (
                                  <span className={`font-semibold ${item.alert ? 'text-red-600' : ''}`}>{item.current_stock}</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center font-medium">{item.minimum_quantity}</TableCell>
                              <TableCell>
                                {item.alert ? (
                                  <Badge className="bg-red-100 text-red-700 border-red-200">
                                    <AlertCircle size={12} className="mr-1" /> Bajo stock
                                  </Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-700 border-green-200">
                                    <CheckCircle size={12} className="mr-1" /> OK
                                  </Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">{item.notes || '-'}</TableCell>
                              {isAdmin && (
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openCriticalDialog(item)}
                                      data-testid={`edit-critical-${item.id}`}>
                                      <Edit size={14} />
                                    </Button>
                                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteCriticalStock(item.id)}
                                      data-testid={`delete-critical-${item.id}`}>
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Card>
                );
              })}
            </div>
          )}

          {/* Alert suggestions */}
          {criticalAlerts.length > 0 && (
            <Card className="border-red-200 bg-red-50">
              <CardHeader>
                <CardTitle className="text-base text-red-800 flex items-center gap-2">
                  <AlertCircle size={18} /> Sugerencias de pedido a proveedor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {criticalAlerts.map(item => (
                  <div key={item.id} className="flex items-center justify-between p-2 bg-white rounded-lg border border-red-100"
                    data-testid={`critical-alert-${item.id}`}>
                    <div>
                      <span className="font-medium text-sm">{item.product_name}</span>
                      <span className="text-xs text-red-600 ml-2">
                        (stock: {item.current_stock} / mín: {item.minimum_quantity})
                      </span>
                      {item.supplier_name && <span className="text-xs text-slate-500 ml-2">· {item.supplier_name}</span>}
                    </div>
                    <Button size="sm" variant="outline" className="text-xs h-7 border-blue-300 text-blue-700 hover:bg-blue-50"
                      onClick={() => { setActiveTab('supplier'); openSupplierDialog(); }}
                      data-testid={`suggest-order-${item.id}`}>
                      <ShoppingCart size={12} className="mr-1" /> Crear pedido
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* Stock por Tiendas Tab */}
      {activeTab === 'store-stock' && (
        <>
          <div className="flex justify-between items-center gap-3 flex-wrap">
            <div>
              <p className="text-sm text-slate-500">
                Define cantidades objetivo por producto en cada ubicación · {storeStock.length} producto{storeStock.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Buscar producto..."
                  value={storeStockSearch}
                  onChange={(e) => setStoreStockSearch(e.target.value)}
                  className="pl-9 h-9 w-56"
                  data-testid="store-stock-search"
                />
              </div>
              {isAdmin && (
                <Button onClick={() => openStoreStockDialog()} className="bg-orange-500 hover:bg-orange-600 text-white gap-2" data-testid="add-store-stock-btn">
                  <Plus size={16} /> Añadir Producto
                </Button>
              )}
            </div>
          </div>

          {storeStock.length === 0 ? (
            <Card>
              <CardContent className="p-10 text-center text-slate-400">
                <Warehouse size={42} className="mx-auto mb-3 text-slate-300" />
                <p className="text-base">No hay productos configurados.</p>
                <p className="text-xs mt-1">Añade un producto para definir cantidades objetivo por tienda y almacén.</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[200px]">Producto</TableHead>
                      <TableHead className="text-center bg-amber-50 min-w-[110px]">
                        <div className="flex items-center justify-center gap-1">
                          <Warehouse size={14} className="text-amber-600" />
                          <span>Almacén</span>
                        </div>
                      </TableHead>
                      {stores.map(store => (
                        <TableHead key={store.id} className="text-center min-w-[110px]">
                          <div className="flex items-center justify-center gap-1">
                            <Store size={14} className="text-orange-500" />
                            <span className="truncate max-w-[120px]" title={store.name}>{store.name}</span>
                          </div>
                        </TableHead>
                      ))}
                      <TableHead className="text-center min-w-[100px]">Total</TableHead>
                      <TableHead className="min-w-[140px]">Notas</TableHead>
                      {isAdmin && <TableHead className="w-20 text-center">Acciones</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {storeStock
                      .filter(it => !storeStockSearch || it.product_name.toLowerCase().includes(storeStockSearch.toLowerCase()))
                      .map(item => {
                        const targets = item.targets || {};
                        const total = Object.values(targets).reduce((s, v) => s + (parseInt(v) || 0), 0);
                        return (
                          <TableRow key={item.id} data-testid={`store-stock-row-${item.id}`}>
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell className="text-center bg-amber-50/40">
                              {isAdmin ? (
                                <Input
                                  type="number"
                                  min="0"
                                  className="w-20 h-7 text-center text-sm mx-auto"
                                  value={targets.warehouse ?? 0}
                                  onChange={(e) => updateStoreStockTarget(item.id, 'warehouse', e.target.value)}
                                  data-testid={`store-stock-warehouse-${item.id}`}
                                />
                              ) : (
                                <span className="font-semibold">{targets.warehouse ?? 0}</span>
                              )}
                            </TableCell>
                            {stores.map(store => (
                              <TableCell key={store.id} className="text-center">
                                {isAdmin ? (
                                  <Input
                                    type="number"
                                    min="0"
                                    className="w-20 h-7 text-center text-sm mx-auto"
                                    value={targets[store.id] ?? 0}
                                    onChange={(e) => updateStoreStockTarget(item.id, store.id, e.target.value)}
                                    data-testid={`store-stock-${store.id}-${item.id}`}
                                  />
                                ) : (
                                  <span className="font-semibold">{targets[store.id] ?? 0}</span>
                                )}
                              </TableCell>
                            ))}
                            <TableCell className="text-center">
                              <Badge variant="outline" className="font-semibold">{total}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500 max-w-[180px] truncate">{item.notes || '-'}</TableCell>
                            {isAdmin && (
                              <TableCell className="text-center">
                                <div className="flex gap-1 justify-center">
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openStoreStockDialog(item)}
                                    data-testid={`edit-store-stock-${item.id}`}>
                                    <Edit size={14} />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setDeleteStoreStockId(item.id)}
                                    data-testid={`delete-store-stock-${item.id}`}>
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </div>
              {stores.length === 0 && (
                <div className="p-4 text-xs text-slate-500 bg-slate-50 border-t border-slate-200">
                  Aún no hay tiendas configuradas. Añade tiendas en la pestaña <strong>Tiendas</strong> para mostrar columnas adicionales.
                </div>
              )}
            </Card>
          )}
        </>
      )}

      {/* Store Stock Dialog */}
      <Dialog open={storeStockDialogOpen} onOpenChange={setStoreStockDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingStoreStock ? 'Editar Producto' : 'Añadir Producto'}</DialogTitle>
            <DialogDescription>
              Define las cantidades objetivo del producto en cada tienda y en el almacén.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre del producto</Label>
              <Input
                value={storeStockForm.product_name}
                onChange={(e) => setStoreStockForm({ ...storeStockForm, product_name: e.target.value })}
                placeholder="Ej: Cámaras 27.5"
                data-testid="store-stock-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Cantidades objetivo por ubicación</Label>
              <div className="space-y-2 border border-slate-200 rounded-lg p-3 bg-slate-50">
                <div className="flex items-center gap-2">
                  <Warehouse size={14} className="text-amber-600" />
                  <span className="flex-1 text-sm">Almacén</span>
                  <Input
                    type="number"
                    min="0"
                    className="w-24 h-8"
                    value={storeStockForm.targets.warehouse ?? 0}
                    onChange={(e) => setStoreStockForm({
                      ...storeStockForm,
                      targets: { ...storeStockForm.targets, warehouse: parseInt(e.target.value) || 0 },
                    })}
                    data-testid="store-stock-form-warehouse"
                  />
                </div>
                {stores.map(store => (
                  <div key={store.id} className="flex items-center gap-2">
                    <Store size={14} className="text-orange-500" />
                    <span className="flex-1 text-sm truncate">{store.name}</span>
                    <Input
                      type="number"
                      min="0"
                      className="w-24 h-8"
                      value={storeStockForm.targets[store.id] ?? 0}
                      onChange={(e) => setStoreStockForm({
                        ...storeStockForm,
                        targets: { ...storeStockForm.targets, [store.id]: parseInt(e.target.value) || 0 },
                      })}
                      data-testid={`store-stock-form-${store.id}`}
                    />
                  </div>
                ))}
                {stores.length === 0 && (
                  <p className="text-xs text-slate-500">No hay tiendas. Añade tiendas en la pestaña Tiendas.</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={storeStockForm.notes}
                onChange={(e) => setStoreStockForm({ ...storeStockForm, notes: e.target.value })}
                rows={2}
                placeholder="Opcional"
                data-testid="store-stock-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoreStockDialogOpen(false)} data-testid="store-stock-cancel">Cancelar</Button>
            <Button onClick={saveStoreStock} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="store-stock-save">
              {editingStoreStock ? 'Actualizar' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete store-stock */}
      <AlertDialog open={!!deleteStoreStockId} onOpenChange={(open) => !open && setDeleteStoreStockId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. Se borrará el producto y todas sus cantidades objetivo.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="store-stock-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteStoreStock} className="bg-red-600 hover:bg-red-700" data-testid="store-stock-delete-confirm">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Dialog open={criticalDialogOpen} onOpenChange={setCriticalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCritical ? 'Editar Material Crítico' : 'Añadir Material Crítico'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Producto (vincular a material existente)</Label>
              <Select value={criticalForm.material_id || 'custom'}
                onValueChange={(v) => {
                  if (v === 'custom') {
                    setCriticalForm({ ...criticalForm, material_id: '', product_name: '' });
                  } else {
                    const mat = materials.find(m => m.id === v);
                    setCriticalForm({ ...criticalForm, material_id: v, product_name: mat?.name || '', category: mat?.category || '' });
                  }
                }}>
                <SelectTrigger data-testid="critical-material-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="custom">Texto libre</SelectItem>
                  {materials.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name} ({m.category})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nombre del producto</Label>
              <Input value={criticalForm.product_name}
                onChange={(e) => setCriticalForm({ ...criticalForm, product_name: e.target.value })}
                placeholder="Nombre del material" data-testid="critical-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Stock actual</Label>
                <Input type="number" min="0" value={criticalForm.current_stock}
                  onChange={(e) => setCriticalForm({ ...criticalForm, current_stock: parseInt(e.target.value) || 0 })}
                  data-testid="critical-current-input" />
              </div>
              <div className="space-y-2">
                <Label>Cantidad mínima</Label>
                <Input type="number" min="1" value={criticalForm.minimum_quantity}
                  onChange={(e) => setCriticalForm({ ...criticalForm, minimum_quantity: parseInt(e.target.value) || 1 })}
                  data-testid="critical-min-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Input value={criticalForm.supplier_name}
                  onChange={(e) => setCriticalForm({ ...criticalForm, supplier_name: e.target.value })}
                  placeholder="Nombre del proveedor" data-testid="critical-supplier-input" />
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Input value={criticalForm.category}
                  onChange={(e) => setCriticalForm({ ...criticalForm, category: e.target.value })}
                  placeholder="Categoría" data-testid="critical-category-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea value={criticalForm.notes}
                onChange={(e) => setCriticalForm({ ...criticalForm, notes: e.target.value })}
                placeholder="Notas adicionales" rows={2} data-testid="critical-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={saveCriticalStock} className="bg-red-600 hover:bg-red-700 text-white" data-testid="save-critical-btn">
              {editingCritical ? 'Actualizar' : 'Añadir'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Store Dialog */}
      <Dialog open={storeDialogOpen} onOpenChange={setStoreDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">
              {editingStore ? 'Editar Tienda' : 'Nueva Tienda'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre *</Label>
              <Input
                value={storeForm.name}
                onChange={(e) => setStoreForm({ ...storeForm, name: e.target.value })}
                placeholder="Ej: Tienda Centro, Almacén General..."
              />
            </div>
            <div className="space-y-2">
              <Label>Descripción</Label>
              <Textarea
                value={storeForm.description}
                onChange={(e) => setStoreForm({ ...storeForm, description: e.target.value })}
                placeholder="Descripción opcional..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStoreDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleSaveStore}>
              {editingStore ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Order Dialog */}
      <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">Nuevo Pedido</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Tienda *</Label>
              <Select value={newOrderStore} onValueChange={setNewOrderStore}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar tienda..." />
                </SelectTrigger>
                <SelectContent>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Materiales</Label>
                <Button type="button" variant="outline" size="sm" onClick={addOrderLine}>
                  <Plus size={14} className="mr-1" /> Añadir Línea
                </Button>
              </div>

              {newOrderLines.map((line, index) => (
                <div key={index} className="bg-slate-50 p-3 rounded-lg space-y-3">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Material (lista o escribir)</Label>
                      <Select 
                        value={line.material_id || 'custom'}
                        onValueChange={(v) => {
                          if (v === 'custom') {
                            updateOrderLine(index, 'material_id', '');
                          } else {
                            updateOrderLine(index, 'material_id', v);
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="custom">✏️ Escribir manualmente</SelectItem>
                          {materials.map(m => (
                            <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-16">
                      <Label className="text-xs">Cant.</Label>
                      <Input
                        type="number"
                        min="1"
                        value={line.quantity}
                        onChange={(e) => updateOrderLine(index, 'quantity', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Prioridad</Label>
                      <Select value={line.priority} onValueChange={(v) => updateOrderLine(index, 'priority', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="urgent">Urgente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-500"
                      onClick={() => removeOrderLine(index)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                  
                  {!line.material_id && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Nombre del material *</Label>
                        <Input
                          value={line.material_name}
                          onChange={(e) => updateOrderLine(index, 'material_name', e.target.value)}
                          placeholder="Nombre..."
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Categoría</Label>
                        <Input
                          value={line.category}
                          onChange={(e) => updateOrderLine(index, 'category', e.target.value)}
                          placeholder="Categoría..."
                        />
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <Label className="text-xs">Comentarios</Label>
                    <Input
                      value={line.comments}
                      onChange={(e) => updateOrderLine(index, 'comments', e.target.value)}
                      placeholder="Ej: Bicicleta de cliente esperando..."
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Notas del pedido</Label>
              <Textarea
                value={newOrderNotes}
                onChange={(e) => setNewOrderNotes(e.target.value)}
                placeholder="Notas adicionales (puede ser texto largo)..."
                rows={4}
                className="resize-y min-h-[60px]"
                data-testid="new-order-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOrderDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={handleCreateOrder}>
              Crear Pedido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Store Confirmation */}
      <Dialog open={!!deleteStoreId} onOpenChange={() => setDeleteStoreId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">Eliminar Tienda</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">¿Estás seguro de que quieres eliminar esta tienda?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteStoreId(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteStore}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Order Confirmation */}
      <Dialog open={!!deleteOrderId} onOpenChange={() => setDeleteOrderId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">Eliminar Pedido</DialogTitle>
          </DialogHeader>
          <p className="text-slate-600">¿Estás seguro de que quieres eliminar este pedido?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteOrderId(null)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDeleteOrder}>Eliminar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Order Dialog */}
      <Dialog open={editOrderDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setEditOrderDialogOpen(false);
          setEditingOrder(null);
        }
      }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-order-modal">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed'] flex items-center gap-2">
              <Edit size={20} className="text-orange-500" />
              Editar Pedido - {editingOrder?.store_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Lines section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-semibold">Materiales del pedido</Label>
                <Button type="button" variant="outline" size="sm" onClick={addEditOrderLine} data-testid="add-edit-line-btn">
                  <Plus size={14} className="mr-1" /> Añadir Línea
                </Button>
              </div>

              {editOrderLines.filter(l => !l._toDelete).map((line, index) => (
                <div key={line.id} className={`bg-slate-50 p-3 rounded-lg space-y-3 ${line._isNew ? 'border-2 border-green-200' : ''}`}>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-xs">Material</Label>
                      {line._isNew ? (
                        <Select
                          value={line.material_id || 'custom'}
                          onValueChange={(v) => {
                            if (v === 'custom') {
                              updateEditOrderLine(index, 'material_id', '');
                            } else {
                              updateEditOrderLine(index, 'material_id', v);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="custom">✏️ Escribir manualmente</SelectItem>
                            {materials.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input
                          value={line.material_name}
                          onChange={(e) => updateEditOrderLine(index, 'material_name', e.target.value)}
                          placeholder="Nombre del material..."
                        />
                      )}
                    </div>
                    <div className="w-20">
                      <Label className="text-xs">Cant.</Label>
                      <Input
                        type="number"
                        min="1"
                        value={line.quantity}
                        onChange={(e) => updateEditOrderLine(index, 'quantity', parseInt(e.target.value) || 1)}
                      />
                    </div>
                    <div className="w-28">
                      <Label className="text-xs">Prioridad</Label>
                      <Select value={line.priority} onValueChange={(v) => updateEditOrderLine(index, 'priority', v)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="normal">Normal</SelectItem>
                          <SelectItem value="urgent">Urgente</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => removeEditOrderLine(index)}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>

                  {line._isNew && !line.material_id && (
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Nombre del material *</Label>
                        <Input
                          value={line.material_name}
                          onChange={(e) => updateEditOrderLine(index, 'material_name', e.target.value)}
                          placeholder="Nombre..."
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Categoría</Label>
                        <Input
                          value={line.category}
                          onChange={(e) => updateEditOrderLine(index, 'category', e.target.value)}
                          placeholder="Categoría..."
                        />
                      </div>
                    </div>
                  )}

                  <div>
                    <Label className="text-xs">Comentarios</Label>
                    <Input
                      value={line.comments}
                      onChange={(e) => updateEditOrderLine(index, 'comments', e.target.value)}
                      placeholder="Ej: Bicicleta de cliente esperando..."
                    />
                  </div>

                  {!line._isNew && (
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span>Estado actual:</span>
                      {getStatusBadge(line.status)}
                    </div>
                  )}
                </div>
              ))}

              {editOrderLines.filter(l => !l._toDelete).length === 0 && (
                <p className="text-sm text-slate-400 text-center py-4">No hay líneas en el pedido</p>
              )}
            </div>

            {/* Notes section */}
            <div className="space-y-2">
              <Label className="font-semibold">Notas del pedido</Label>
              <Textarea
                value={editOrderNotes}
                onChange={(e) => setEditOrderNotes(e.target.value)}
                placeholder="Notas adicionales (puede ser texto largo)..."
                rows={4}
                className="resize-y min-h-[80px]"
                data-testid="edit-order-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOrderDialogOpen(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-orange-500 hover:bg-orange-600 text-white gap-2" 
              onClick={handleSaveEditedOrder}
              disabled={savingOrder}
              data-testid="save-edit-order-btn"
            >
              {savingOrder ? (
                <>
                  <div className="spinner w-4 h-4" />
                  Guardando...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Guardar Cambios
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Order Dialog */}
      <Dialog open={supplierDialogOpen} onOpenChange={(open) => { setSupplierDialogOpen(open); if (!open) setEditingSupplier(null); }}>
        <DialogContent className="max-w-lg" data-testid="supplier-order-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-['Barlow_Condensed']">{editingSupplier ? 'Editar Pedido a Proveedor' : 'Nuevo Pedido a Proveedor'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Proveedor *</Label>
                <Input value={supplierForm.supplier} onChange={e => setSupplierForm({...supplierForm, supplier: e.target.value})} placeholder="Nombre del proveedor" data-testid="supplier-name-input" />
              </div>
              <div className="col-span-2">
                <Label>Producto / Material *</Label>
                <Input value={supplierForm.product_description} onChange={e => setSupplierForm({...supplierForm, product_description: e.target.value})} placeholder="Descripción del material" data-testid="supplier-product-input" />
              </div>
              <div>
                <Label>Cantidad *</Label>
                <Input type="number" min="1" value={supplierForm.quantity} onChange={e => setSupplierForm({...supplierForm, quantity: e.target.value})} data-testid="supplier-qty-input" />
              </div>
              <div>
                <Label>Precio estimado</Label>
                <Input type="number" step="0.01" value={supplierForm.estimated_price} onChange={e => setSupplierForm({...supplierForm, estimated_price: e.target.value})} placeholder="0.00" data-testid="supplier-price-input" />
              </div>
              <div>
                <Label>Responsable *</Label>
                <Select value={supplierForm.responsible_user_id} onValueChange={v => {
                  const u = users.find(u => u.id === v);
                  setSupplierForm({...supplierForm, responsible_user_id: v, responsible_user_name: u?.name || ''});
                }}>
                  <SelectTrigger data-testid="supplier-responsible-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fecha prevista</Label>
                <Input type="date" value={supplierForm.expected_date} onChange={e => setSupplierForm({...supplierForm, expected_date: e.target.value})} data-testid="supplier-date-input" />
              </div>
              <div className="col-span-2">
                <Label>Observaciones</Label>
                <Textarea value={supplierForm.notes} onChange={e => setSupplierForm({...supplierForm, notes: e.target.value})} placeholder="Notas adicionales..." rows={2} data-testid="supplier-notes-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupplierDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={handleSaveSupplier} data-testid="save-supplier-btn">
              <Save size={14} className="mr-1" /> {editingSupplier ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Supplier Order Confirm */}
      <AlertDialog open={!!deleteSupplierOrder} onOpenChange={() => { setDeleteSupplierOrder(null); setDeleteReason(''); }}>
        <AlertDialogContent data-testid="delete-supplier-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar pedido a proveedor</AlertDialogTitle>
            <AlertDialogDescription>
              El pedido será eliminado lógicamente y podrá restaurarse.
              {(isAdmin || isSuperAdmin) && (
                <div className="mt-3">
                  <Label className="text-sm font-medium text-slate-700">Motivo de eliminación *</Label>
                  <Textarea
                    value={deleteReason}
                    onChange={e => setDeleteReason(e.target.value)}
                    placeholder="Indica el motivo..."
                    rows={2}
                    className="mt-1"
                    data-testid="delete-reason-input"
                  />
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteSupplier} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-supplier">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
